import { useRouter } from 'next/router'
import { Trans, useTranslation } from 'next-i18next'
import { without } from 'ramda'

import {
  Heading1,
  LinkGrid,
  LinkText,
  Paragraph,
  Section,
  Section1,
  Section2
} from '@/features/Listings/seo/elements'
import { Label } from '@/components/text'

import { getPopularCitiesByState, toTitleCase } from './utils'

export const utilityTypes = [
  'cable-tv',
  'utilities-included',
  'water-included',
  'heat-included',
  'gas-included',
  'wifi-internet-included'
]

const propertyTypes = [
  'apartments',
  'townhouses',
  'houses',
  'rooms',
  'studios',
  '1-bedroom',
  '2-bedroom',
  '3-bedroom',
  '4-bedroom'
]

export const isUtilityFilterPage = (slug: string) => {
  if (!slug) return false
  return utilityTypes.some(utility => slug.includes(utility))
}

const UtilitiesFilterContent = ({
  state,
  citySlug,
  cityName,
  filterTitle,
  filterSlug,
  isCanadaPage,
  cityNameWithState
}: {
  state: string
  citySlug: string
  cityName: string
  filterTitle: string
  filterSlug: string
  isCanadaPage: boolean
  cityNameWithState: string
}) => {
  const { t } = useTranslation()
  const router = useRouter()
  const isChinese = router.locale === 'zh'
  const cityLinks = getPopularCitiesByState(state)

  const amenityContent: Record<
    string,
    { primary: { key: string; default: string }; secondary: string }
  > = {
    'cable-tv': {
      primary: {
        key: 'seo:cable-tv utility filter paragraph',
        default:
          'Staying connected and entertained is easier when your rent includes cable TV. That’s why you’re searching for {{ filterTitle }} in {{ cityName }}, {{ stateName }}. With cable service already covered, you can skip the hassle of setting up accounts or dealing with extra monthly bills—just move in and start watching your favourite shows right away.'
      },
      secondary: t(
        'seo:cable-tv utility filter paragraph 2',
        'Your next home with the convenience of cable TV included is just a click away!'
      )
    },
    'utilities-included': {
      primary: {
        key: 'seo:utilities utility filter paragraph',
        default:
          'Monthly expenses are easier to manage when your rent includes utilities. That’s why you’re searching for {{ filterTitle }} in {{ cityName }}, {{ stateName }}. With hydro and electricity already covered, you can avoid surprise bills and enjoy a more predictable rental budget—just move in and relax.'
      },
      secondary: t(
        'seo:utilities utility filter paragraph 2',
        'Your next home with utilities included is just a click away!'
      )
    },
    'water-included': {
      primary: {
        key: 'seo:water utility filter paragraph',
        default:
          'Saving on monthly bills is easier when your rent includes water. That’s why you’re searching for {{ filterTitle }} in {{ cityName }}, {{ stateName }}. With water costs already covered by the landlord, you can enjoy one less utility to worry about and more predictable monthly expenses.'
      },
      secondary: t(
        'seo:water utility filter paragraph 2',
        'Your next home with water included is just a click away!'
      )
    },
    'heat-included': {
      primary: {
        key: 'seo:heat utility filter paragraph',
        default:
          'Staying warm through the colder months is easier when your rent includes heat. That’s why you’re searching for {{ filterTitle }} in {{ cityName }}, {{ stateName }}. With heating already covered by the landlord, you can avoid high utility bills and enjoy peace of mind during winter.'
      },
      secondary: t(
        'seo:heat utility filter paragraph 2',
        'Your next home with heat included is just a click away!'
      )
    },
    'gas-included': {
      primary: {
        key: 'seo:gas utility filter paragraph',
        default:
          'Managing your monthly budget is easier when your rent includes gas. That’s why you’re searching for {{ filterTitle }} in {{ cityName }}, {{ stateName }}. Whether it’s for heating, hot water, or cooking, having natural gas already covered means fewer bills and less hassle.'
      },
      secondary: t(
        'seo:gas utility filter paragraph 2',
        'Your next home with gas included is just a click away!'
      )
    },
    'wifi-internet-included': {
      primary: {
        key: 'seo:wifi utility filter paragraph',
        default:
          'Staying connected is essential, and having internet included in your rent makes life that much easier. That’s why you’re searching for {{ filterTitle }} in {{ cityName }}, {{ stateName }}. Whether you work from home, stream shows, or game online, built-in internet means no setup fees or separate bills.'
      },
      secondary: t(
        'seo:wifi utility filter paragraph 2',
        'Your next home with internet included is just a click away!'
      )
    }
  }

  const utilityFilter =
    utilityTypes.find(slug => filterSlug?.includes(slug)) || null

  const propertyFilter =
    propertyTypes.find(slug => filterSlug?.includes(slug)) || null

  const propertyString = t(
    `filters:b.listing.view.${propertyFilter || 'apartments'}.text`
  )

  if (!utilityFilter) return null

  return (
    <Section>
      <Section1>
        <Section2>
          <Heading1>
            {cityName}
            {isChinese ? '' : ' '}
            {toTitleCase(filterTitle)}
          </Heading1>
          <Paragraph>
            <Trans
              i18nKey={amenityContent[utilityFilter].primary.key}
              values={{
                filterTitle: filterTitle?.toLowerCase(),
                cityName,
                stateName: state
              }}
              defaults={amenityContent[utilityFilter].primary.default}
            />
          </Paragraph>
          <Paragraph>
            {t(
              'seo:Browse available rentals to see photos, floor plans, and details on amenities, neighborhoods, transit options, and more.'
            )}{' '}
            {amenityContent[utilityFilter].secondary}
          </Paragraph>
        </Section2>
      </Section1>
      <Section1>
        <Section2>
          <Heading1>
            {isCanadaPage ? (
              <Trans
                i18nKey="seo:Popular Amenities in Canada"
                values={{
                  filterTitle: toTitleCase(filterTitle)
                }}
                defaults="{{ filterTitle }} in Canada"
              />
            ) : (
              <Trans
                i18nKey="seo:Popular Amenities Nearby"
                values={{
                  filterTitle: toTitleCase(filterTitle)
                }}
                defaults="Nearby {{ filterTitle }}"
              />
            )}
          </Heading1>
        </Section2>
        <LinkGrid linksOnly>
          {without([citySlug], cityLinks).map(slug => {
            const name = t(`cities:city_name_${slug}`)
            return (
              <Label key={slug} fontSize="14px">
                <LinkText href={`/rental-listings/city/${slug}/${filterSlug}`}>
                  {isCanadaPage ? (
                    <Trans
                      i18nKey="seo:amenity for rent in city"
                      values={{
                        filterTitle,
                        cityName: name
                      }}
                      defaults="{{ filterTitle }} in {{ cityName }}"
                    />
                  ) : (
                    <Trans
                      i18nKey="seo:amenity for rent in city state"
                      values={{
                        filterTitle,
                        cityName: name,
                        stateName: state
                      }}
                      defaults="{{ filterTitle }} in {{ cityName }}, {{ stateName }}"
                    />
                  )}
                </LinkText>
              </Label>
            )
          })}
        </LinkGrid>
      </Section1>
      {!isCanadaPage && (
        <Section1>
          <Section2>
            <Heading1>
              <Trans
                i18nKey="seo:utilities for rent in city"
                values={{
                  propertyType: propertyString,
                  cityNameWithState
                }}
                defaults="{{ propertyType }} for Rent in {{ cityNameWithState }} with Your Preferred Utilities"
              />
            </Heading1>
          </Section2>
          <LinkGrid linksOnly>
            {without([utilityFilter], utilityTypes).map(slug => {
              const amenityString = t(`filters:b.listing.view.${slug}.text`)
              return (
                <Label key={slug} fontSize="14px">
                  <LinkText
                    href={`/rental-listings/city/${citySlug}/${
                      propertyFilter ? `${propertyFilter}-${slug}` : slug
                    }/`}
                  >
                    <Trans
                      i18nKey="seo:property type for rent in city with amenity"
                      values={{
                        propertyType: propertyString,
                        cityNameWithState,
                        amenity: amenityString?.toLowerCase()
                      }}
                      defaults="{{ propertyType }} for rent in {{ cityNameWithState }} with {{ amenity }}"
                    />
                  </LinkText>
                </Label>
              )
            })}
          </LinkGrid>
        </Section1>
      )}
    </Section>
  )
}

export default UtilitiesFilterContent
